import { createContext, useContext, useState, useEffect } from "react";

const STORAGE_KEY = "biba_compare_items";
export const MAX_COMPARE_ITEMS = 4;

// Every "Add to Compare" call site normalizes tixaru's synced General Specs
// table (array of {item, specification, unit}) into this same
// {label, value} shape, so the compare page's spec rows work identically
// no matter which page a product was added from.
export const generalSpecsToRows = (generalSpecs) =>
  Array.isArray(generalSpecs)
    ? generalSpecs
        .filter((r) => r && r.item)
        .map((r) => ({
          label: r.item,
          value: r.unit ? `${r.specification ?? ""} ${r.unit}`.trim() : String(r.specification ?? ""),
        }))
    : [];

const CompareContext = createContext();

const loadInitial = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CompareProvider = ({ children }) => {
  const [compareItems, setCompareItems] = useState(loadInitial);
  const [limitMsg, setLimitMsg] = useState("");

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(compareItems));
    } catch {
      // localStorage unavailable (private browsing, quota) -- the compare
      // list just won't survive a reload, not worth surfacing to the user.
    }
  }, [compareItems]);

  // Keyed by "id" (the product's slug/route -- always unique) rather than
  // Part No, since some rows have no Part No field filled in on Tixaru and
  // several blank ones would otherwise collide into a single compare slot.
  const isInCompare = (id) => compareItems.some((p) => p.id === id);

  const addToCompare = (product) => {
    setLimitMsg("");
    setCompareItems((prev) => {
      if (prev.some((p) => p.id === product.id)) return prev;
      if (prev.length >= MAX_COMPARE_ITEMS) {
        setLimitMsg(`You can compare up to ${MAX_COMPARE_ITEMS} products at a time.`);
        return prev;
      }
      return [...prev, product];
    });
  };

  const removeFromCompare = (id) => {
    setCompareItems((prev) => prev.filter((p) => p.id !== id));
  };

  const toggleCompare = (product) => {
    if (isInCompare(product.id)) {
      removeFromCompare(product.id);
    } else {
      addToCompare(product);
    }
  };

  const clearCompare = () => {
    setCompareItems([]);
    setLimitMsg("");
  };

  return (
    <CompareContext.Provider
      value={{
        compareItems,
        isInCompare,
        addToCompare,
        removeFromCompare,
        toggleCompare,
        clearCompare,
        limitMsg,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => useContext(CompareContext);
